import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected, getActiveIntegrations } from '@helpers/integrations';
import { getGoogleOnboardingOAuthUrl } from '@constants/integrations';
import { googleIntegrationService, companyService, brandsService } from '@services';
import { alertActions, accountActions, googleActions, authActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { RoutePath } from '@constants/routes';
import { BRAND_TEAL } from '@constants/colors';
import ViewInit from './ViewInit';
import ViewGoogleEmpty from './ViewGoogleEmpty';
import ViewManual from './ViewManual';
import ViewGoogle from './ViewGoogle';
import ViewCompleted from './ViewCompleted';
import ImportLoadingOverlay from '@components/ImportLoadingOverlay';
import { useTranslation, useQueryParams } from '@hooks';
import GTManager, { buildUserParams } from '@widgets/gtm';
import CircularProgress from '@mui/material/CircularProgress';
import { Container } from './styles';

const VIEW = {
    INIT: 1,
    GOOGLE_EMPTY: 2,
    GOOGLE: 3,
    GOOGLE_RIGHTS_PROBLEM: 4,
    COMPLETED: 5,
    MANUAL: 6,
};

const GOOGLE_ERROR = {
    CANCEL: 'access_denied',
};

const BRAND_SETUP_STATUS = { Running: 0, Ready: 1, Applied: 2, Failed: 3, Applying: 4 };
const BRAND_SETUP_WAIT_MS = 7 * 60 * 1000;
const BRAND_SETUP_POLL_MS = 3000;
const BRAND_SETUP_MAX_FAILED_POLLS = 3;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const ignoreFailure = request =>
    Promise.resolve()
        .then(request)
        .catch(() => null);

const isBrandSetupSettled = run =>
    run?.status === BRAND_SETUP_STATUS.Applied || run?.status === BRAND_SETUP_STATUS.Failed;

const isBrandSetupInProgress = run =>
    run?.status === BRAND_SETUP_STATUS.Running || run?.status === BRAND_SETUP_STATUS.Applying;

const startBrandSetup = async locations => {
    const run = await ignoreFailure(() => brandsService.startSetup(locations));
    return run?.id ? { id: run.id, deadline: Date.now() + BRAND_SETUP_WAIT_MS } : null;
};

const finishBrandSetup = async setup => {
    if (!setup) return;
    let run = await ignoreFailure(() => brandsService.completeSetupImports(setup.id));
    let failedPolls = run ? 0 : 1;
    while (!isBrandSetupSettled(run) && failedPolls < BRAND_SETUP_MAX_FAILED_POLLS && Date.now() < setup.deadline) {
        await wait(BRAND_SETUP_POLL_MS);
        const previous = run;
        run = await ignoreFailure(() =>
            isBrandSetupInProgress(previous)
                ? brandsService.getSetupRun(setup.id)
                : brandsService.completeSetupImports(setup.id),
        );
        failedPolls = run ? 0 : failedPolls + 1;
    }
};

const SecondStep = ({ account, onboardingType }) => {
    const { t } = useTranslation();
    const [view, setView] = useState();
    const [isImporting, setIsImporting] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [isFinishing, setIsFinishing] = useState(false);
    const [connectedIntegrationId, setConnectedIntegrationId] = useState(null);
    const dispatch = useDispatch();
    const query = useQueryParams();
    const navigate = useNavigate();
    const user = useSelector(state => state.auth?.user);

    const GOOGLE_CONNECTED = isIntegrationConnected(account, AccIntegrationName.Google);

    useEffect(() => {
        const code = query.get('code');
        const error = query.get('error');
        if (code && account) {
            setView(VIEW.GOOGLE);
            connectGoogleIntegration(code);
        } else if (error === GOOGLE_ERROR.CANCEL) {
            navigate(RoutePath.Onboarding, { replace: true });
        } else if (!view && !isImporting) {
            if (account?.onboardingStatus === VIEW.INIT && GOOGLE_CONNECTED) {
                loadGoogleLocations();
            } else {
                setView(account?.onboardingStatus);
            }
        }
    }, [query, account]);

    const importAndFinish = async locations => {
        setIsImporting(true);
        setProgress({ current: 0, total: locations.length });
        const brandSetup = startBrandSetup(locations);
        const { imported, failed } = await companyService.importGoogleCompaniesWithProgress(
            locations,
            (current, total) => setProgress({ current, total }),
        );
        if (failed.length) {
            handleApiError({ errorCode: failed[0].reason }, 'alertMessages.importFail');
            if (imported === 0) {
                setIsImporting(false);
                return false;
            }
        }
        setIsFinishing(true);
        await finishBrandSetup(await brandSetup);
        setIsFinishing(false);
        try {
            const updatedAcc = await dispatch(accountActions.get());
            if (!updatedAcc?.id) throw new Error('Failed to load account');
            await dispatch(
                accountActions.updateAccount({ ...updatedAcc, onboardingCompleted: true, onboardingStatus: 5 }),
            );
            GTManager.push('onboarding-completed', {
                ...buildUserParams(user),
                account_id: updatedAcc.id,
                registration_type: GTManager.getRegistrationType(),
            });
            await dispatch(authActions.getCurrentUser());
            dispatch(googleActions.reset());
        } catch (error) {
            handleApiError(error);
            setIsImporting(false);
            return false;
        }
        setIsImporting(false);
        return true;
    };

    const loadGoogleLocations = async () => {
        try {
            const locations = await dispatch(googleActions.get());
            if (!Array.isArray(locations)) throw new Error('Failed to load locations');
            if (locations.length === 0) {
                await dispatch(accountActions.updateAccount({ ...account, onboardingStatus: VIEW.GOOGLE_EMPTY }));
                setView(VIEW.GOOGLE_EMPTY);
            } else if (locations.length === 1) {
                const success = await importAndFinish(locations);
                if (!success) {
                    await dispatch(accountActions.updateAccount({ ...account, onboardingStatus: VIEW.GOOGLE }));
                    setView(VIEW.GOOGLE);
                }
            } else {
                await dispatch(accountActions.updateAccount({ ...account, onboardingStatus: VIEW.GOOGLE }));
                setView(VIEW.GOOGLE);
            }
        } catch (error) {
            handleApiError(error);
            setView(VIEW.INIT);
        }
    };

    const onAddManuallyClick = () => setView(VIEW.MANUAL);

    /* if locations exist and Google connected -> show user select locations view
        if Google disconnected -> try to connect
    */
    const onImportFromGoogleClick = () => {
        if (!GOOGLE_CONNECTED) {
            window.location = getGoogleOnboardingOAuthUrl();
        } else {
            loadGoogleLocations();
        }
    };

    // Onboarding is single-Google-account: discard whatever Google account is currently connected before
    // starting OAuth for another one, so reconnecting REPLACES it instead of adding a second integration.
    // Source the ids from the (refreshed) account — not the in-memory connectedIntegrationId, which is
    // lost on reload — and revoke each one scoped (never the unscoped full disconnect). The connect just
    // made may not be on the refetched account yet, so include connectedIntegrationId defensively.
    const onConnectAnotherAccountClick = async () => {
        try {
            const updatedAcc = await dispatch(accountActions.get());
            const googleIntegrationIds = new Set(
                getActiveIntegrations(updatedAcc, AccIntegrationName.Google).map(i => i.id),
            );
            if (connectedIntegrationId) {
                googleIntegrationIds.add(connectedIntegrationId);
            }
            for (const id of googleIntegrationIds) {
                await googleIntegrationService.revokeGoogleIntegration(id);
            }
            setConnectedIntegrationId(null);
            await dispatch(accountActions.updateAccount({ ...updatedAcc, onboardingStatus: VIEW.INIT }));
            window.location = getGoogleOnboardingOAuthUrl();
        } catch (error) {
            dispatch(alertActions.error(t('apiErrors.google_access_revoke_error')));
        }
    };

    const connectGoogleIntegration = async code => {
        try {
            const { integrationId } = await googleIntegrationService.authenticateGoogleIntegration({
                accountId: account?.id,
                code,
                redirectUri: `${window.location.origin}${RoutePath.Onboarding}`,
            });
            setConnectedIntegrationId(integrationId);
            navigate(RoutePath.Onboarding, { replace: true });
            const locations = await dispatch(googleActions.get(integrationId));
            if (!Array.isArray(locations)) throw new Error('Failed to load locations');
            if (locations.length === 0) {
                await dispatch(accountActions.updateAccount({ ...account, onboardingStatus: VIEW.GOOGLE_EMPTY }));
                setView(VIEW.GOOGLE_EMPTY);
            } else if (locations.length === 1) {
                const success = await importAndFinish(locations);
                if (!success) {
                    await dispatch(accountActions.updateAccount({ ...account, onboardingStatus: VIEW.GOOGLE }));
                    setView(VIEW.GOOGLE);
                }
            } else {
                await dispatch(accountActions.updateAccount({ ...account, onboardingStatus: VIEW.GOOGLE }));
            }
        } catch (error) {
            navigate(RoutePath.Onboarding, { replace: true });
            setView(VIEW.INIT);
            handleApiError(error);
        }
    };

    return (
        <>
            <ImportLoadingOverlay
                open={isImporting}
                current={progress.current}
                total={isFinishing ? undefined : progress.total}
                messages={isFinishing ? [t('importLoading.almostDone')] : undefined}
            />
            <Container
                isNotMain={
                    !view || view === VIEW.INIT || view === VIEW.GOOGLE_EMPTY || view === VIEW.GOOGLE_RIGHTS_PROBLEM
                }
            >
                {!view && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '48px 24px',
                        }}
                    >
                        <CircularProgress
                            sx={{ color: BRAND_TEAL, width: '58px !important', height: '58px !important' }}
                        />
                    </div>
                )}
                {(view === VIEW.INIT || view === VIEW.GOOGLE_RIGHTS_PROBLEM) && (
                    <ViewInit
                        onboardingType={onboardingType}
                        onImportFromGoogleClick={onImportFromGoogleClick}
                        onAddManuallyClick={onAddManuallyClick}
                    />
                )}
                {view === VIEW.GOOGLE_EMPTY && (
                    <ViewGoogleEmpty
                        onboardingType={onboardingType}
                        onConnectAnotherAccountClick={onConnectAnotherAccountClick}
                        onAddManuallyClick={onAddManuallyClick}
                    />
                )}
                {view === VIEW.MANUAL && <ViewManual onImportFromGoogleClick={onImportFromGoogleClick} />}
                {view === VIEW.GOOGLE && (
                    <ViewGoogle
                        onboardingType={onboardingType}
                        onConnectAnotherAccountClick={onConnectAnotherAccountClick}
                        onAddManuallyClick={onAddManuallyClick}
                        onImportCompanies={importAndFinish}
                    />
                )}
                {view === VIEW.COMPLETED && <ViewCompleted />}
            </Container>
        </>
    );
};

export default SecondStep;
